import { useState } from "react";
import { X, Loader2, Send, User, Mail, Phone, MapPin, Truck } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function QuoteModal({ isOpen, onClose, config, brand, selection, pricing, sku }) {
  const [fd, setFd] = useState({ first_name: "", last_name: "", email: "", phone: "", address: "", zip_code: "", city: "" });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  if (!isOpen) return null;

  const steps = config?.steps || [];
  const isAerofoils = brand === "aerofoils";
  const fp = (p) => new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR", minimumFractionDigits: 0 }).format(p);
  const pHt = pricing?.price_ht;
  const tva = pricing?.tva_amount;
  const pTtc = pricing?.price_ttc;

  const getLabel = (stepId) => {
    const stepDef = steps.find(s => s.id === stepId);
    if (!stepDef) return "-";
    const dataKey = stepDef.data_key || stepId;
    const options = config[dataKey] || [];
    const val = selection[stepId];
    if (!val) return "-";
    if (isAerofoils) {
      if (stepId === "serie") return config.series?.find(s => s.id === val)?.name || val;
      if (stepId === "couleur") return config.colors?.find(c => c.id === val)?.name || val;
      if (stepId === "aile") return `${val} cm2`;
      if (stepId === "batterie") return config.batteries?.find(b => b.id === val)?.name || val;
      if (stepId === "chargeur") return config.chargers?.find(c => c.id === val)?.name || val;
    }
    const opt = options.find(o => o.id === val);
    return opt?.name || val;
  };

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const selectionLabels = {};
      steps.forEach(s => { selectionLabels[s.label] = getLabel(s.id); });
      await axios.post(`${API}/quotes`, {
        ...fd,
        brand,
        selections: selection,
        selection_labels: selectionLabels,
        price_ht: pHt,
        tva_amount: tva,
        price: pTtc,
        price_label: "Prix TTC",
        sku,
      });
      setSuccess(true);
      toast.success("Demande envoyee !");
      setTimeout(() => { onClose(); setSuccess(false); setFd({ first_name: "", last_name: "", email: "", phone: "", address: "", zip_code: "", city: "" }); }, 2000);
    } catch { toast.error("Erreur"); } finally { setLoading(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="quote-modal">
      <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" onClick={onClose}></div>
      <div className="relative w-full max-w-lg bg-[#0D1F0D] border border-[#1A3A1A] rounded-2xl shadow-2xl overflow-hidden max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-[#1A3A1A]">
          <h2 className="text-xl font-bold text-white">Demander un devis {config?.brand_name || ""}</h2>
          <button onClick={onClose} className="p-2 text-[#888] hover:text-white" data-testid="quote-modal-close"><X className="w-5 h-5" /></button>
        </div>
        {success ? (
          <div className="p-8 text-center" data-testid="quote-success">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-[#39FF14]/20 flex items-center justify-center">
              <Send className="w-8 h-8 text-[#39FF14]" />
            </div>
            <h3 className="text-xl font-bold text-white mb-2">Envoyee !</h3>
            <p className="text-[#888]">Merci {fd.first_name}</p>
          </div>
        ) : (
          <>
            <div className="p-6 bg-[#0B0B0B] border-b border-[#1A3A1A]">
              <p className="text-xs text-[#888] uppercase mb-3">Configuration {config?.brand_name || ""}</p>
              <div className="grid grid-cols-2 gap-2 text-sm">
                {steps.map(s => (
                  <div key={s.id}>
                    <span className="text-[#888]">{s.label}:</span>
                    <span className="text-white ml-2">{getLabel(s.id)}</span>
                  </div>
                ))}
              </div>
              {/* Price breakdown */}
              <div className="mt-4 pt-4 border-t border-[#1A3A1A] space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-xs text-[#888] uppercase">Prix HT</span>
                  <span className="text-lg font-bold text-white">{pHt != null ? fp(pHt) : "\u2014"}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-xs text-[#888]">TVA 20%</span>
                  <span className="text-sm text-[#888]">{tva != null ? `+ ${fp(tva)}` : "\u2014"}</span>
                </div>
                <div className="flex items-center justify-between pt-2 border-t border-[#1A3A1A]">
                  <span className="text-xs text-[#39FF14] uppercase font-bold">Prix TTC</span>
                  <span className="text-2xl font-bold text-[#39FF14]">{pTtc != null ? fp(pTtc) : "\u2014"}</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <Truck className="w-3.5 h-3.5 text-[#888]" />
                  <span className="text-xs text-[#888]">Hors frais de livraison</span>
                </div>
              </div>
            </div>
            <form onSubmit={submit} className="p-6 space-y-4" data-testid="quote-form">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-[#888] mb-2">Prenom *</label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#888]" />
                    <input type="text" required value={fd.first_name} onChange={e => setFd({ ...fd, first_name: e.target.value })} className="w-full pl-10 pr-4 py-3 bg-[#0B0B0B] border border-[#1A3A1A] rounded-lg text-white focus:border-[#39FF14] focus:outline-none" placeholder="Jean" data-testid="quote-first-name" />
                  </div>
                </div>
                <div>
                  <label className="block text-sm text-[#888] mb-2">Nom *</label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#888]" />
                    <input type="text" required value={fd.last_name} onChange={e => setFd({ ...fd, last_name: e.target.value })} className="w-full pl-10 pr-4 py-3 bg-[#0B0B0B] border border-[#1A3A1A] rounded-lg text-white focus:border-[#39FF14] focus:outline-none" placeholder="Dupont" data-testid="quote-last-name" />
                  </div>
                </div>
              </div>
              <div>
                <label className="block text-sm text-[#888] mb-2">Email *</label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#888]" />
                  <input type="email" required value={fd.email} onChange={e => setFd({ ...fd, email: e.target.value })} className="w-full pl-10 pr-4 py-3 bg-[#0B0B0B] border border-[#1A3A1A] rounded-lg text-white focus:border-[#39FF14] focus:outline-none" placeholder="jean@email.com" data-testid="quote-email" />
                </div>
              </div>
              <div>
                <label className="block text-sm text-[#888] mb-2">Telephone *</label>
                <div className="relative">
                  <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#888]" />
                  <input type="tel" required value={fd.phone} onChange={e => setFd({ ...fd, phone: e.target.value })} className="w-full pl-10 pr-4 py-3 bg-[#0B0B0B] border border-[#1A3A1A] rounded-lg text-white focus:border-[#39FF14] focus:outline-none" placeholder="06 12 34 56 78" data-testid="quote-phone" />
                </div>
              </div>
              <div className="pt-2 border-t border-[#1A3A1A]">
                <p className="text-xs text-[#888] mb-3 uppercase">Adresse (facultatif)</p>
                <div className="space-y-3">
                  <div>
                    <div className="relative">
                      <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#888]" />
                      <input type="text" value={fd.address} onChange={e => setFd({ ...fd, address: e.target.value })} className="w-full pl-10 pr-4 py-3 bg-[#0B0B0B] border border-[#1A3A1A] rounded-lg text-white focus:border-[#39FF14] focus:outline-none" placeholder="Adresse postale" data-testid="quote-address" />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <input type="text" value={fd.zip_code} onChange={e => setFd({ ...fd, zip_code: e.target.value })} className="w-full px-4 py-3 bg-[#0B0B0B] border border-[#1A3A1A] rounded-lg text-white focus:border-[#39FF14] focus:outline-none" placeholder="Code postal" data-testid="quote-zipcode" />
                    </div>
                    <div>
                      <input type="text" value={fd.city} onChange={e => setFd({ ...fd, city: e.target.value })} className="w-full px-4 py-3 bg-[#0B0B0B] border border-[#1A3A1A] rounded-lg text-white focus:border-[#39FF14] focus:outline-none" placeholder="Ville" data-testid="quote-city" />
                    </div>
                  </div>
                </div>
              </div>
              <button type="submit" disabled={loading} data-testid="quote-submit-btn" className="w-full flex items-center justify-center gap-2 py-4 bg-[#39FF14] text-black font-bold rounded-lg hover:bg-[#2BC410] disabled:opacity-50 shadow-neon">
                {loading ? <><Loader2 className="w-5 h-5 animate-spin" />Envoi...</> : <><Send className="w-5 h-5" />Envoyer</>}
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
